import { enhanceError } from '@allurion/utils';
import { Message } from '@twilio/conversations';
import { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from 'src/analytics/analytics';
import { toastError } from 'src/components/ui/toasts';
import { ParsedMessageNotification } from 'src/domain/Conversations';
import { Logger } from 'src/services/Logger';

import TwilioConversationsService from './TwilioConversationsService';

export function useTwilioIncommingMessages(identity?: string) {
  const intl = useIntl();

  const [incomingMessage, setIncomingMessage] = useState<ParsedMessageNotification | undefined>(
    undefined
  );
  const [hasConnectionError, setHasConnectionError] = useState(false);
  const { trackTwilioChatFailure } = useTrackEvent();

  // `intl` and `trackTwilioChatFailure` get new identities on (nearly) every
  // render (`useTrackEvent` returns a fresh object each time). Keeping them in a
  // ref lets the subscription effect below depend only on `identity`, so we don't
  // tear down and re-establish the whole Twilio connection on unrelated renders.
  const onSubscribeErrorRef = useRef<(error: unknown) => void>(() => {});

  onSubscribeErrorRef.current = (error: unknown) => {
    Logger.captureException(enhanceError(error, { prefix: 'useTwilioIncommingMessages' }));
    reportConnectionLost(error);
  };

  // Shared by the initial-subscribe failure above and the mid-session loss
  // below. The subscribe path additionally reports to Sentry; the mid-session
  // path does not, because the service has already reported the underlying
  // connectionError and doing it here would file the same failure twice.
  function reportConnectionLost(error: unknown) {
    setHasConnectionError(true);
    trackTwilioChatFailure({ error });
    toastError(
      intl.formatMessage({
        defaultMessage:
          'Cannot receive incoming messages. If you want to be notified of new messages, please reload the page.',
        id: 'twilio.failed-to-subscribe-to-incoming-messages',
      })
    );
  }

  const onConnectionLostRef = useRef<(error: unknown) => void>(() => {});

  onConnectionLostRef.current = reportConnectionLost;

  // Until this existed, the toast above was reachable only when the *first*
  // connect failed. A connection lost mid-session -- an expired access token is
  // the common one -- left the provider on a page that had silently stopped
  // receiving patient messages, with nothing shown and no reconnect.
  useEffect(() => {
    // Gated on identity for the same reason the subscribe effect below is: with
    // no identity this hook never subscribed to anything, and the service is a
    // module singleton, so a terminal error raised by another consumer of it
    // would otherwise tell this user their incoming messages had stopped when
    // this hook was never receiving any.
    if (!identity) {
      return;
    }

    return TwilioConversationsService.onConnectionLost((error) =>
      onConnectionLostRef.current(error)
    );
  }, [identity]);

  useEffect(() => {
    if (!identity) {
      return;
    }

    Logger.debug(`[twilio] Subscribing to incoming messages ${identity}`);

    // `subscribe()` awaits connect() before it registers the callback, so a fast
    // identity change or unmount can run this effect's cleanup (unsubscribe)
    // *before* subscribe() finishes and re-adds the callback. Track cancellation
    // so the callback stays inert and we undo any late re-registration.
    let cancelled = false;

    TwilioConversationsService.subscribe(identity, (message: Message) => {
      if (cancelled) {
        return;
      }

      const { body, conversation, author, attributes } = message || {};

      const { name: title } = attributes as any;
      const { patientId } = conversation?.attributes as any;
      // This will work as long as the created by of the conversations is the patient id?
      const isPatientMessage = author === conversation.createdBy;

      const parsedMessage: ParsedMessageNotification = {
        title,
        body: body || '',
        author: author || '',
        patientId,
        isPatientMessage,
      };

      setIncomingMessage(parsedMessage);
    })
      .then(() => {
        // If cleanup already ran while subscribe() was awaiting connect(), the
        // callback was re-added after unsubscribe — remove it again.
        if (cancelled) {
          TwilioConversationsService.unsubscribe(identity);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          onSubscribeErrorRef.current(error);
        }
      });

    return () => {
      cancelled = true;
      TwilioConversationsService.unsubscribe(identity);
    };
  }, [identity]);

  const clearIncomingMessage = () => {
    setIncomingMessage(undefined);
  };

  return {
    clearIncomingMessage,
    incomingMessage,
    hasConnectionError,
  };
}
